/**
 * Sprint 2 (Iter-220m) — Incomplete Onboarding Queue
 *
 * Admin surface for users who signed up but never finished onboarding.
 * Pairs with /admin/talent-approval (queue of finished profiles awaiting
 * admin review) — this page is one step earlier in the funnel.
 *
 * Capabilities:
 *   • Search by name, email, phone, company.
 *   • Filter by role (talent / client / all), stuck step, signup age.
 *   • Toggle CSV-imported / non-self-signups in/out of the queue.
 *   • Send onboarding reminder (email + SMS).
 *   • Recover orphan (Donna Taylor-style) — re-creates missing profile row.
 *   • Archive or hard-delete abandoned accounts.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  ArrowLeft, Search, Loader2, RefreshCw, Mail, MessageSquare, Wrench,
  Trash2, AlertTriangle, UserCheck, ChevronRight, Users, Building2,
  Sparkles, Clock, Filter, X, ShieldAlert, ArrowLeftRight,
} from 'lucide-react';
import { toast } from 'sonner';

import { formatDate } from '../../utils/timeFormat';
const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STEP_LABELS = {
  orphan_no_profile: { label: 'Orphan — No Profile', cls: 'bg-red-100 text-red-800 border-red-200' },
  possibly_wrong_role: { label: 'Possibly Wrong Role', cls: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  basic_info: { label: 'Missing Basic Info', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  location: { label: 'Missing Location', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  profile_photo: { label: 'Missing Photo', cls: 'bg-orange-100 text-orange-800 border-orange-200' },
  idv: { label: 'IDV Not Submitted', cls: 'bg-purple-100 text-purple-800 border-purple-200' },
  idv_rejected: { label: 'IDV Rejected', cls: 'bg-red-100 text-red-700 border-red-200' },
  awaiting_approval: { label: 'Awaiting Approval', cls: 'bg-cyan-100 text-cyan-800 border-cyan-200' },
  rejected: { label: 'Rejected', cls: 'bg-gray-200 text-gray-700 border-gray-300' },
  complete: { label: 'Complete', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  // Iter-281 Phase B Part 3 — talents with corrupted names and NO StaffConnect rescue record.
  name_corruption_no_sc_match: { label: 'Name Corruption · Manual Review', cls: 'bg-rose-100 text-rose-800 border-rose-200' },
};

function StepBadge({ step }) {
  const s = STEP_LABELS[step] || { label: step || '—', cls: 'bg-gray-100 text-gray-700 border-gray-200' };
  return <Badge data-testid={`step-badge-${step}`} className={`text-[10px] uppercase tracking-wider border ${s.cls}`}>{s.label}</Badge>;
}

export default function IncompleteOnboarding() {
  const navigate = useNavigate();
  const [role, setRole] = useState('talent');
  const [q, setQ] = useState('');
  const [stuckFilter, setStuckFilter] = useState('');
  const [daysMin, setDaysMin] = useState('');
  const [includeCsv, setIncludeCsv] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [users, setUsers] = useState([]);
  const [buckets, setBuckets] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ role, limit: '200' });
      if (q.trim()) params.set('q', q.trim());
      if (stuckFilter) params.set('stuck_step', stuckFilter);
      if (daysMin) params.set('days_since_signup_min', daysMin);
      if (includeCsv) params.set('include_csv_imported', 'true');
      const [listRes, statsRes] = await Promise.all([
        fetch(`${API}/api/admin/incomplete-onboarding?${params}`, { headers: headers() }),
        fetch(`${API}/api/admin/incomplete-onboarding/stats${includeCsv ? '?include_csv_imported=true' : ''}`, { headers: headers() }),
      ]);
      const list = await listRes.json();
      const st = await statsRes.json();
      setUsers(list.users || []);
      setBuckets(list.buckets || {});
      setStats(st || null);
    } catch (e) {
      toast.error('Failed to load incomplete onboarding queue');
    } finally {
      setLoading(false);
    }
  }, [role, q, stuckFilter, daysMin, includeCsv, includeArchived]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Debounce free-text search (re-run fetch via dependency on q)
  useEffect(() => {
    const t = setTimeout(() => { /* fetchData re-runs via dep array */ }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const sendReminder = async (user) => {
    setActingId(user.user_id);
    try {
      const res = await fetch(`${API}/api/admin/incomplete-onboarding/${user.user_id}/send-reminder`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel: 'both' }),
      });
      const data = await res.json();
      if (res.ok) {
        const channels = [];
        if (data.sent?.email) channels.push('email');
        if (data.sent?.sms) channels.push('SMS');
        toast.success(channels.length ? `Reminder sent via ${channels.join(' + ')}` : 'Reminder dispatched');
        fetchData();
      } else toast.error(data.detail || 'Failed to send reminder');
    } catch { toast.error('Failed to send reminder'); }
    finally { setActingId(null); }
  };

  const recover = async (user) => {
    if (!window.confirm(`Recover orphan account for ${user.email}? This re-creates the missing profile row so the user can log back in.`)) return;
    setActingId(user.user_id);
    try {
      const res = await fetch(`${API}/api/admin/incomplete-onboarding/${user.user_id}/recover`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ create_missing_profile: true }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.profile_created ? 'Profile created — user can resume onboarding' : 'Account reactivated');
        fetchData();
      } else toast.error(data.detail || 'Recovery failed');
    } catch { toast.error('Recovery failed'); }
    finally { setActingId(null); }
  };

  const archive = async (user, hard = false) => {
    const msg = hard
      ? `HARD DELETE ${user.email}? This permanently removes the account and is only safe for ghost rows.`
      : `Archive ${user.email}? They will be unable to log in until reactivated.`;
    if (!window.confirm(msg)) return;
    setActingId(user.user_id);
    try {
      const res = await fetch(`${API}/api/admin/incomplete-onboarding/${user.user_id}${hard ? '?hard=true' : ''}`, {
        method: 'DELETE',
        headers: headers(),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(hard ? 'Account hard-deleted' : 'Account archived');
        fetchData();
      } else toast.error(data.detail || 'Action failed');
    } catch { toast.error('Action failed'); }
    finally { setActingId(null); }
  };

  // Iter-225k — Account Type Conversion. Lets a super-admin migrate a
  // user who registered under the wrong role without losing their data.
  const [convertTarget, setConvertTarget] = useState(null);
  const [convertNewRole, setConvertNewRole] = useState('client');
  const [convertCompany, setConvertCompany] = useState('');
  const [convertContact, setConvertContact] = useState('');
  const [convertPreview, setConvertPreview] = useState(null);
  const [convertLoading, setConvertLoading] = useState(false);

  const openConvert = (user) => {
    setConvertTarget(user);
    const defaultRole = user.role === 'talent' ? 'client' : 'talent';
    setConvertNewRole(defaultRole);
    setConvertCompany(user.company_name || '');
    setConvertContact(user.name || '');
    setConvertPreview(null);
  };

  const closeConvert = () => {
    setConvertTarget(null);
    setConvertPreview(null);
  };

  const loadConvertPreview = async (roleOverride) => {
    if (!convertTarget) return;
    const targetRole = roleOverride || convertNewRole;
    try {
      const res = await fetch(`${API}/api/admin/users/${convertTarget.user_id}/convert-role/preview?new_role=${targetRole}`, { headers: headers() });
      const data = await res.json();
      if (res.ok) setConvertPreview(data);
      else toast.error(data.detail || 'Preview failed');
    } catch { toast.error('Preview failed'); }
  };

  const submitConvert = async () => {
    if (!convertTarget) return;
    setConvertLoading(true);
    try {
      const body = { new_role: convertNewRole, overrides: {} };
      if (convertCompany) body.overrides.company_name = convertCompany;
      if (convertContact) body.overrides.contact_name = convertContact;
      const res = await fetch(`${API}/api/admin/users/${convertTarget.user_id}/convert-role`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Converted to ${data.new_role}. The user must log in again to pick up the new role.`);
        closeConvert();
        fetchData();
      } else toast.error(data.detail || 'Conversion failed');
    } catch { toast.error('Conversion failed'); }
    finally { setConvertLoading(false); }
  };

  const stepOptions = useMemo(
    () => Object.entries(buckets).sort((a, b) => b[1] - a[1]),
    [buckets]
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50" data-testid="incomplete-onboarding-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/home')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-amber-500" /> Incomplete Onboarding
              </h1>
              <p className="text-[11px] text-gray-500 hidden sm:block">Users who signed up but haven't finished — search, nudge, recover, or remove.</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4">
        {/* Stats cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="p-4 flex items-center gap-3">
                <Users className="w-8 h-8 text-amber-500" />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-amber-700">Talent Incomplete</p>
                  <p className="text-xl font-bold text-amber-900" data-testid="stat-talent-incomplete">{stats.talent_incomplete}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-cyan-200 bg-cyan-50/50">
              <CardContent className="p-4 flex items-center gap-3">
                <Building2 className="w-8 h-8 text-cyan-500" />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-cyan-700">Client Incomplete</p>
                  <p className="text-xl font-bold text-cyan-900" data-testid="stat-client-incomplete">{stats.client_incomplete}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-purple-200 bg-purple-50/50">
              <CardContent className="p-4 flex items-center gap-3">
                <Building2 className="w-8 h-8 text-purple-500" />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-purple-700">Agency Incomplete</p>
                  <p className="text-xl font-bold text-purple-900" data-testid="stat-agency-incomplete">{stats.agency_incomplete ?? 0}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-indigo-200 bg-indigo-50/50">
              <CardContent className="p-4 flex items-center gap-3">
                <ArrowLeftRight className="w-8 h-8 text-indigo-500" />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-indigo-700">Possibly Wrong Role</p>
                  <p className="text-xl font-bold text-indigo-900" data-testid="stat-wrong-role">{stats.possibly_wrong_role ?? 0}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50/50">
              <CardContent className="p-4 flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-red-500" />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-red-700">Orphans Total</p>
                  <p className="text-xl font-bold text-red-900" data-testid="stat-orphans">{stats.total_orphans}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="border-gray-200">
          <CardContent className="p-3 sm:p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Filter className="w-4 h-4 text-cyan-600" /> Filters
              </div>
              <Tabs value={role} onValueChange={setRole} className="">
                <TabsList className="h-8">
                  <TabsTrigger value="talent" className="text-xs px-3" data-testid="filter-role-talent">Talent</TabsTrigger>
                  <TabsTrigger value="client" className="text-xs px-3" data-testid="filter-role-client">Client</TabsTrigger>
                  <TabsTrigger value="agency" className="text-xs px-3" data-testid="filter-role-agency">Agency</TabsTrigger>
                  <TabsTrigger value="all" className="text-xs px-3" data-testid="filter-role-all">All</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="relative col-span-1 sm:col-span-2">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="filter-search"
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  placeholder="Search name, email, phone, company…"
                  className="pl-9 h-9"
                />
              </div>
              <div>
                <Label className="text-[10px] text-gray-500 uppercase tracking-wider mb-1 block">Stuck Step</Label>
                <select
                  data-testid="filter-stuck-step"
                  value={stuckFilter}
                  onChange={e => setStuckFilter(e.target.value)}
                  className="w-full h-9 px-2 text-sm rounded-md border border-gray-200 bg-white"
                >
                  <option value="">All steps</option>
                  {Object.keys(STEP_LABELS).map(k => (
                    <option key={k} value={k} label={`${STEP_LABELS[k].label}${buckets[k] ? ` (${buckets[k]})` : ''}`} />
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-[10px] text-gray-500 uppercase tracking-wider mb-1 block">Older than (days)</Label>
                <Input
                  data-testid="filter-days-min"
                  type="number"
                  min={0}
                  value={daysMin}
                  onChange={e => setDaysMin(e.target.value)}
                  placeholder="e.g. 7"
                  className="h-9"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-1">
              <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                <Checkbox checked={includeCsv} onCheckedChange={setIncludeCsv} data-testid="filter-include-csv" />
                Include CSV-imported / non-self-signups
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                <Checkbox checked={includeArchived} onCheckedChange={setIncludeArchived} data-testid="filter-include-archived" />
                Include archived ("disappeared") accounts
              </label>
              {(q || stuckFilter || daysMin || includeCsv || includeArchived) && (
                <Button
                  data-testid="clear-filters"
                  size="sm"
                  variant="ghost"
                  onClick={() => { setQ(''); setStuckFilter(''); setDaysMin(''); setIncludeCsv(false); setIncludeArchived(false); }}
                  className="h-7 text-xs text-gray-500"
                >
                  <X className="w-3.5 h-3.5 mr-1" /> Clear
                </Button>
              )}
            </div>

            {/* Bucket chips */}
            {stepOptions.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {stepOptions.map(([k, n]) => (
                  <button
                    key={k}
                    data-testid={`bucket-chip-${k}`}
                    onClick={() => setStuckFilter(stuckFilter === k ? '' : k)}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-all ${
                      stuckFilter === k
                        ? 'bg-cyan-600 text-white border-cyan-600'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-cyan-300'
                    }`}
                  >
                    {STEP_LABELS[k]?.label || k} · {n}
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* List */}
        {loading ? (
          <div className="py-16 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
          </div>
        ) : users.length === 0 ? (
          <Card className="border-gray-200">
            <CardContent className="py-16 text-center text-sm text-gray-500" data-testid="empty-state">
              <UserCheck className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
              No incomplete onboardings match your filters. Nice cleanup!
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {users.map(u => (
              <Card
                key={u.user_id}
                data-testid={`onboarding-row-${u.user_id}`}
                className={`border ${u.is_orphan ? 'border-red-200 bg-red-50/40' : 'border-gray-200 bg-white'} hover:shadow-md transition-shadow`}
              >
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:gap-4 gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {u.name || u.company_name || u.email}
                          </p>
                          <p className="text-[11px] text-gray-500 truncate">{u.email} {u.phone ? ` · ${u.phone}` : ''}</p>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                          <Badge variant="outline" className="text-[10px] uppercase">{u.role}</Badge>
                          <StepBadge step={u.stuck_step} />
                          {u.is_orphan && (
                            <Badge className="text-[10px] uppercase bg-red-100 text-red-800 border border-red-200">Orphan</Badge>
                          )}
                        </div>
                      </div>

                      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                        <div className="bg-gray-50 rounded px-2 py-1">
                          <p className="text-gray-500 uppercase tracking-wider text-[9px]">Signed up</p>
                          <p className="text-gray-800 font-medium">{u.days_since_signup}d ago</p>
                        </div>
                        <div className="bg-gray-50 rounded px-2 py-1">
                          <p className="text-gray-500 uppercase tracking-wider text-[9px]">Last login</p>
                          <p className="text-gray-800 font-medium">{u.last_login_at ? formatDate(u.last_login_at) : 'Never'}</p>
                        </div>
                        <div className="bg-gray-50 rounded px-2 py-1">
                          <p className="text-gray-500 uppercase tracking-wider text-[9px]">Profile %</p>
                          <p className="text-gray-800 font-medium">{u.profile_completion_percent || 0}%</p>
                        </div>
                        <div className="bg-gray-50 rounded px-2 py-1">
                          <p className="text-gray-500 uppercase tracking-wider text-[9px]">Reminders</p>
                          <p className="text-gray-800 font-medium">{u.reminders_sent || 0} / 2</p>
                        </div>
                      </div>

                      {u.blockers?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {u.blockers.map((b, i) => (
                            <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-100">
                              {b}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap lg:flex-col gap-2 lg:w-auto w-full">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={actingId === u.user_id}
                        onClick={() => sendReminder(u)}
                        data-testid={`send-reminder-${u.user_id}`}
                        className="flex-1 lg:flex-none border-cyan-200 text-cyan-700 hover:bg-cyan-50"
                      >
                        {actingId === u.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Mail className="w-3.5 h-3.5 mr-1" />}
                        Reminder
                      </Button>
                      {u.is_orphan && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={actingId === u.user_id}
                          onClick={() => recover(u)}
                          data-testid={`recover-${u.user_id}`}
                          className="flex-1 lg:flex-none border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                        >
                          <Wrench className="w-3.5 h-3.5 mr-1" /> Recover
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={actingId === u.user_id}
                        onClick={() => archive(u, false)}
                        data-testid={`archive-${u.user_id}`}
                        className="flex-1 lg:flex-none border-gray-200 text-gray-700 hover:bg-gray-100"
                      >
                        Archive
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={actingId === u.user_id}
                        onClick={() => openConvert(u)}
                        data-testid={`convert-role-${u.user_id}`}
                        className="flex-1 lg:flex-none border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                      >
                        <ArrowLeftRight className="w-3.5 h-3.5 mr-1" /> Convert
                      </Button>
                      {u.is_orphan && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={actingId === u.user_id}
                          onClick={() => archive(u, true)}
                          data-testid={`hard-delete-${u.user_id}`}
                          className="flex-1 lg:flex-none border-red-200 text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Iter-225k — Account Type Conversion modal */}
      {convertTarget && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          data-testid="convert-role-modal"
          onClick={closeConvert}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Convert account type</h3>
                <p className="text-xs text-gray-600 mt-1 break-all">{convertTarget.email}</p>
                <p className="text-[11px] text-gray-500 mt-1">Currently: <span className="font-medium uppercase">{convertTarget.role}</span></p>
              </div>
              <Button size="icon" variant="ghost" onClick={closeConvert} data-testid="convert-close-btn">
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-xs">Convert to</Label>
                <select
                  value={convertNewRole}
                  onChange={(e) => { setConvertNewRole(e.target.value); setConvertPreview(null); }}
                  data-testid="convert-new-role-select"
                  className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                >
                  {['talent', 'client', 'agency']
                    .filter((r) => r !== convertTarget.role)
                    .map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                </select>
              </div>

              {(convertNewRole === 'client' || convertNewRole === 'agency') && (
                <>
                  <div>
                    <Label className="text-xs">Company name</Label>
                    <Input
                      value={convertCompany}
                      onChange={(e) => setConvertCompany(e.target.value)}
                      placeholder="e.g. Acme Events"
                      data-testid="convert-company-input"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Contact name</Label>
                    <Input
                      value={convertContact}
                      onChange={(e) => setConvertContact(e.target.value)}
                      placeholder="Primary contact"
                      data-testid="convert-contact-input"
                      className="mt-1"
                    />
                  </div>
                </>
              )}

              <Button
                size="sm"
                variant="outline"
                onClick={() => loadConvertPreview()}
                className="w-full"
                data-testid="convert-preview-btn"
              >
                Preview impact
              </Button>

              {convertPreview && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2.5 text-[11px] text-amber-900 space-y-1.5">
                  <div className="font-medium">Will create a {convertNewRole} profile seeded with:</div>
                  <pre className="bg-white border border-amber-100 rounded p-2 text-[10px] overflow-x-auto">{JSON.stringify(convertPreview.seed_for_new_profile, null, 2)}</pre>
                  {convertPreview.warnings?.length > 0 && (
                    <ul className="list-disc list-inside space-y-0.5">
                      {convertPreview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  )}
                </div>
              )}

              <div className="bg-gray-50 border border-gray-200 rounded p-2.5 text-[11px] text-gray-700">
                The user's bookings, messages, applications, and history stay attached because the user_id never changes. The user must log in again to pick up the new role.
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={closeConvert} className="flex-1" data-testid="convert-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={submitConvert}
                disabled={convertLoading}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="convert-submit-btn"
              >
                {convertLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <ArrowLeftRight className="w-3.5 h-3.5 mr-1" />}
                Convert to {convertNewRole}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

